import { useEffect, useMemo, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  Copy,
  Download,
  FileText,
  Layers3,
  Maximize2,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Pause,
  Play,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  X,
  PlugZap,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { temperatureLabel, reasoningLabel } from "./modelLabels";
import {
  request,
  MAX_COMPARISON_MODELS,
  type Bootstrap,
  type Obj,
  type Prompt,
  type Suite,
  type Model,
  saved,
  money,
  duration,
  number,
  label,
  evaluation,
  checkpointFindings,
  promptForSuite,
  outcome,
} from "./api";
import { Badge, Modal, Field, ErrorBox, Loading, Empty } from "./ui";
import {
  Connections,
  ModelManager,
  ProfilePicker,
  ProfileSelectDialog,
} from "./Models";
import { LibraryImport, PromptEditor, ScenarioEditor } from "./Library";
import { Analytics } from "./Analytics";
import { GlobalAnalytics } from "./GlobalAnalytics";
import { ThemeSwitcher } from "./Theme";
import { ComparisonSetup } from "./ComparisonSetup";
import { Reevaluate } from "./Reevaluate";
import { DesignIcon } from "./DesignIcon";
import "./bench.css";
const freshConfig = {
  name: "Untitled comparison",
  prompt_id: "",
  suite_id: "",
  baseline_id: "",
  candidate_ids: [] as string[],
  judge_model_id: "",
  simulator_model_id: "",
  realism_model_id: "",
  mode: "replay",
  model_schedule: "simultaneous",
  profile: "quality",
  concurrency: 1,
  measured_seconds: 60,
  max_conversations: 12,
  max_customer_turns: null,
  max_requests: 200,
  budget_usd: 5,
  deadline_seconds: 60,
  retries: 1,
  think_time_ms: 0,
  clock_date: new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date()),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  qualification_requested: false,
};
const activeStates = ["queued", "running", "evaluating", "stopping"];
export default function Workspace() {
  const qc = useQueryClient();
  const boot = useQuery({
    queryKey: ["bench-bootstrap"],
    queryFn: () => request<Bootstrap>("/bootstrap"),
    refetchInterval: 7000,
    retry: 1,
  });
  const [config, setConfig] = useState<Obj>(() =>
    saved("scribe10-comparison-draft", freshConfig),
  );
  const [runId, setRunId] = useState<string>(() =>
    saved("scribe10-open-comparison", ""),
  );
  const [tab, setTab] = useState(() => saved("scribe10-workspace-tab", "compare"));
  const [modal, setModal] = useState("");
  const [caseIndex, setCaseIndex] = useState(0);
  const [promptTab, setPromptTab] = useState("source");
  const [fullPrompt, setFullPrompt] = useState(false);
  const [promptVisible, setPromptVisible] = useState(true);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [judgeOpen, setJudgeOpen] = useState(false);
  const [focusModel, setFocusModel] = useState("");
  const [promptSearch, setPromptSearch] = useState("");
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState<{
    model: string;
    turn: number;
    quote: string;
  } | null>(null);
  const [reviewModel, setReviewModel] = useState("");
  const [manageTarget, setManageTarget] = useState<number | null>(null);
  const [manageRole, setManageRole] = useState("");
  const [managerReturn, setManagerReturn] = useState("");
  const [copiedFrom, setCopiedFrom] = useState("");
  const [draftWarnings, setDraftWarnings] = useState<string[]>([]);
  const [previousDraft, setPreviousDraft] = useState<Obj | null>(() => saved("scribe10-previous-draft", null));
  const [newPrompt, setNewPrompt] = useState(false);
  const [newSuite, setNewSuite] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [showDraftSources, setShowDraftSources] = useState<boolean>(() =>
    saved("scribe10-show-draft-sources", false),
  );
  const runQ = useQuery({
    queryKey: ["bench-run", runId],
    queryFn: () => request("/runs/" + runId),
    enabled: !!runId,
    refetchInterval: (q) =>
      activeStates.includes(q.state.data?.status) ? 1500 : false,
    retry: 1,
  });
  const promptQ = useQuery({
    queryKey: ["bench-prompt", config.prompt_id],
    queryFn: () => request<Prompt>("/prompts/" + config.prompt_id),
    enabled: !!config.prompt_id && !runId,
    staleTime: Infinity,
    retry: 1,
  });
  const suiteQ = useQuery({
    queryKey: ["bench-suite", config.suite_id],
    queryFn: () => request<Suite>("/suites/" + config.suite_id),
    enabled: !!config.suite_id && !runId,
    staleTime: Infinity,
    retry: 1,
  });
  const data = boot.data;
  const run = runId ? runQ.data || null : null;
  const active = !!run && activeStates.includes(run.status);
  const patch = (k: string, v: unknown) => setConfig((c) => ({ ...c, [k]: v }));
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["bench-bootstrap"] });
    if (runId) qc.invalidateQueries({ queryKey: ["bench-run", runId] });
  };
  useEffect(() => { localStorage.setItem("scribe10-previous-draft", JSON.stringify(previousDraft)); }, [previousDraft]);
  useEffect(() => { localStorage.setItem("scribe10-workspace-tab", JSON.stringify(tab)); }, [tab]);
  useEffect(() => {
    localStorage.setItem("scribe10-comparison-draft", JSON.stringify(config));
  }, [config]);
  useEffect(() => {
    localStorage.setItem(
      "scribe10-show-draft-sources",
      JSON.stringify(showDraftSources),
    );
  }, [showDraftSources]);
  useEffect(() => {
    localStorage.setItem("scribe10-open-comparison", JSON.stringify(runId));
    setCaseIndex(0);
    setSelectedEvidence(null);
    setFocusModel("");
    setReviewModel("");
  }, [runId]);
  useEffect(() => {
    if (!data || initialized) return;
    setInitialized(true);
    setConfig((c) => {
      const p = data.prompts.find((p) => p.id === c.prompt_id);
      const s = data.suites.find((s) => s.id === c.suite_id);
      const availableModelIds = new Set(data.models.map((model) => model.id));
      const availableModel = (id: unknown) => typeof id === "string" && availableModelIds.has(id) ? id : "";
      return {
        ...freshConfig,
        ...c,
        prompt_id: p?.id || "",
        suite_id: s?.id || "",
        baseline_id: availableModel(c.baseline_id),
        candidate_ids: Array.isArray(c.candidate_ids) ? c.candidate_ids.filter((id: unknown) => typeof id === "string" && availableModelIds.has(id)) : [],
        judge_model_id: availableModel(c.judge_model_id),
        simulator_model_id: availableModel(c.simulator_model_id),
        realism_model_id: availableModel(c.realism_model_id),
      };
    });
  }, [data, initialized, showDraftSources]);
  const prompt: Prompt | undefined =
    run?.manifest?.prompt ||
    promptQ.data ||
    data?.prompts.find((p) => p.id === config.prompt_id);
  const promptLoading =
    !runId && !!config.prompt_id && !promptQ.data && !promptQ.error;
  const promptReady = !!run?.manifest?.prompt || !!promptQ.data;
  const selectablePrompts = data?.prompts.slice() || [];
  if (
    !run &&
    promptQ.data &&
    !promptQ.data.archived_at &&
    !selectablePrompts.some((p) => p.id === promptQ.data.id)
  )
    selectablePrompts.push(promptQ.data);
  const suite: Suite | undefined =
    run?.manifest?.suite ||
    suiteQ.data ||
    data?.suites.find((s) => s.id === config.suite_id);
  const suiteLoading =
    !runId && !!config.suite_id && !suiteQ.data && !suiteQ.error;
  const suiteReady = !!run?.manifest?.suite || !!suiteQ.data;
  const selectableSuites = data?.suites.slice() || [];
  if (
    !run &&
    suiteQ.data &&
    !suiteQ.data.archived_at &&
    !selectableSuites.some((s) => s.id === suiteQ.data.id)
  )
    selectableSuites.push(suiteQ.data);
  const cases = suite?.cases || [];
  const scenario = cases[Math.max(0, Math.min(caseIndex, cases.length - 1))];
  const models: Model[] =
    run?.manifest?.target_models ||
    (data?.models
      ? ([config.baseline_id, ...config.candidate_ids]
          .map((id) => data.models.find((m) => m.id === id))
          .filter(Boolean) as Model[])
      : []);
  const comparisonCanvas = useRef<HTMLElement>(null);
  const draftSlots = data
    ? [config.baseline_id, ...config.candidate_ids].map((id) =>
        data.models.find((m) => m.id === id),
      )
    : [];
  const displayedModels = focusModel
    ? models.filter((m) => m.id === focusModel)
    : run
      ? models
      : draftSlots;
  const conversations: Obj[] = run?.conversations || [];
  const caseConvs = conversations.filter((c) => c.case?.id === scenario?.id);
  const convFor = (id: string) => caseConvs.find((c) => c.model_id === id);
  const mode = run?.config?.mode || config.mode;
  const currentConfig = run?.config || config;
  const selectedSource = run?.manifest?.rendered_prompts?.[scenario?.id];
  const rendered =
    typeof selectedSource === "string"
      ? selectedSource
      : selectedSource?.system ||
        selectedSource?.content ||
        scenario?.frozen_rendered_prompt;
  const sourceText = promptTab === "rendered" ? rendered : prompt?.content;
  const completeness =
    promptTab === "source"
      ? promptReady
        ? "Complete source"
        : promptLoading
          ? "Loading complete source…"
          : "Select a prompt to load its complete source"
      : rendered
        ? selectedSource?.system_complete ||
          selectedSource?.complete ||
          scenario?.rendered_prompt_complete
          ? "Complete frozen request prompt"
          : "Historical preview · completeness unverified"
        : "Rendered snapshot available after a library import or saved comparison";
  const checkpoints: Obj[] = scenario?.checkpoints?.length
    ? scenario.checkpoints
    : (scenario?.user_turns || []).map((user_turn: string, i: number) => ({
        id: String(i + 1),
        user_turn,
        history: scenario?.history || [],
      }));
  const maxTurns = Math.max(
    checkpoints.length,
    ...caseConvs.map((c) => c.turns?.length || 0),
    1,
  );
  const gridStyle = {
    "--model-count": Math.max(displayedModels.length, 1),
  } as React.CSSProperties;
  const baseline = run?.manifest?.target_models?.[0]?.id || config.baseline_id;
  const judge = run
    ? (run.evaluation_config ? run.evaluation_config.judge_model : run.manifest?.judge_model)
    : data?.models.find((m) => m.id === config.judge_model_id);
  const judgeId = judge?.id || "";
  const overlap = models.some(
    (m) =>
      m.id === judgeId ||
      (m.model_id === judge?.model_id &&
        m.connection_id === judge?.connection_id),
  );
  const providerOverlap = models.some(
    (m) => m.connection_id === judge?.connection_id,
  );
  const waiting = run?.progress?.waiting_reason;
  function updateModel(index: number, id: string) {
    if (index < 0 || index >= MAX_COMPARISON_MODELS) return;
    if (index === 0) patch("baseline_id", id);
    else {
      const ids = [...config.candidate_ids];
      ids[index - 1] = id;
      patch("candidate_ids", ids);
    }
  }
  function draft() {
    setRunId("");
    setTab("compare");
    setSelectedEvidence(null);
    setFocusModel("");
    setError(null);
    setManageTarget(null);
    setManageRole("");
    setManagerReturn("");
    setPromptVisible(true);
    setFullPrompt(false);
    setPromptSearch("");
  }
  async function copyComparison() {
    if (!run || busy) return;
    setBusy(true);
    setError(null);
    try {
      const copy = await request("/runs/" + run.id + "/draft", {});
      await qc.invalidateQueries({ queryKey: ["bench-bootstrap"] });
      setPreviousDraft(structuredClone(config));
      setConfig({ ...freshConfig, ...copy.config, candidate_ids: [...(copy.config.candidate_ids || [])] });
      setCopiedFrom(run.name);
      setDraftWarnings(copy.warnings || []);
      draft();
      setModal("setup");
      toast.success("Comparison copied. Choose models and judges.");
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  function manageProfile(role: string, index?: number, returnTo = "setup") {
    setManageRole(role === "target" ? "" : role);
    setManageTarget(role === "target" ? index ?? 0 : null);
    setManagerReturn(returnTo);
    setModal("models");
  }
  function newComparison() {
    setPreviousDraft(structuredClone(config));
    setConfig((c) => ({
      ...c,
      name: freshConfig.name,
      prompt_id: "",
      suite_id: "",
    }));
    setShowDraftSources(false);
    setCopiedFrom("");
    setDraftWarnings([]);
    setCaseIndex(0);
    draft();
  }
  async function start() {
    setBusy(true);
    setError(null);
    try {
      const r = await request("/runs", {
        ...config,
        judge_model_id: config.judge_model_id || null,
        simulator_model_id: config.mode === "adaptive" ? config.simulator_model_id || null : null,
        realism_model_id: config.mode === "adaptive" ? config.realism_model_id || null : null,
        client_request_id: crypto.randomUUID(),
      });
      setRunId(r.id);
      setTab("compare");
      refresh();
      toast.success("Comparison started.");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function runAction(action: string) {
    setBusy(true);
    setError(null);
    try {
      await request("/runs/" + runId + "/" + action, {});
      refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const preflight: string[] = useMemo(() => {
    if (!data) return [];
    const errors: string[] = [];
    if (!config.prompt_id) errors.push("Choose a prompt");
    if (config.prompt_id && !promptReady)
      errors.push(
        promptQ.error
          ? "Complete prompt could not load"
          : "Loading complete prompt",
      );
    if (!config.suite_id) errors.push("Choose a scenario set");
    if (!config.baseline_id) errors.push("Choose a baseline");
    if (config.candidate_ids.some((id: string) => !id)) errors.push("Choose a model for each candidate slot or remove the empty slot");
    if (config.candidate_ids.length >= MAX_COMPARISON_MODELS)
      errors.push(`Select up to ${MAX_COMPARISON_MODELS} model profiles in total, including the baseline`);
    if (config.suite_id && !suiteReady)
      errors.push(
        suiteQ.error
          ? "Scenario details could not load"
          : "Loading complete scenario details",
      );
    const ids = [config.baseline_id, ...config.candidate_ids].filter(Boolean);
    if (new Set(ids).size !== ids.length)
      errors.push(
        "Use distinct profiles. Clone a model to compare different settings.",
      );
    if (config.mode === "adaptive" && !config.simulator_model_id)
      errors.push("Choose a customer simulator for adaptive conversations");
    for (const id of [
      ...ids,
      config.judge_model_id,
      config.mode === "adaptive" ? config.simulator_model_id : null,
      config.mode === "adaptive" ? config.realism_model_id : null,
    ].filter(Boolean)) {
      const m = data.models.find((m) => m.id === id);
      const c = data.connections.find((c) => c.id === m?.connection_id);
      if (!m) { errors.push("A selected profile is unavailable. Choose a saved model profile."); continue; }
      if (c?.requires_key && !c.keys.some((k) => k.available))
        errors.push(m.name + ": API key unavailable");
      if (
        c?.provider !== "demo" &&
        (m.price_input_per_million == null ||
          m.price_output_per_million == null)
      )
        errors.push(m.name + ": add saved rates for the spending cap");
    }
    const p = data.prompts.find((p) => p.id === config.prompt_id),
      s = data.suites.find((s) => s.id === config.suite_id);
    if (
      p &&
      s &&
      (p.campaign !== s.campaign ||
        p.language?.toLowerCase() !== s.language?.toLowerCase())
    )
      errors.push(
        "Select a prompt matching this scenario set’s campaign and language",
      );
    return [...new Set(errors)];
  }, [data, config, suiteReady, suiteQ.error, promptReady, promptQ.error]);
  if (runId && runQ.isLoading)
    return (
      <div className="bench-app">
        <Loading text="Opening the frozen comparison and its evidence…" />
      </div>
    );
  if (runId && runQ.error && !run)
    return (
      <div className="bench-app">
        <ErrorBox error={runQ.error} />
        <button className="b-button" onClick={() => runQ.refetch()}>
          Retry Comparison
        </button>
        <button className="b-button" onClick={draft}>
          Open Draft
        </button>
      </div>
    );
  if (boot.isLoading)
    return (
      <div className="bench-app">
        <Loading text="Opening the local comparison workspace…" />
      </div>
    );
  if (!data)
    return (
      <div className="bench-app">
        <ErrorBox error={boot.error} />
        <button className="b-button" onClick={() => boot.refetch()}>
          Retry Connection
        </button>
      </div>
    );
  return (
    <div
      className={
        "bench-app " +
        (tab === "compare" && run
          ? judgeOpen
            ? "with-judge-dock open"
            : "with-judge-dock"
          : "")
      }
    >
      <div className="bench-app-grid">
      <aside className="bench-sidebar">
        <a className="cleargrid-brand" href="/" aria-label="ClearGrid Scribe">
          <img src="/cleargrid-lockup-light.svg" className="cleargrid-logo-light" width="120.615" height="32" alt="ClearGrid" />
          <img src="/cleargrid-lockup-dark.svg" className="cleargrid-logo-dark" width="120.615" height="32" alt="ClearGrid" />
          <span className="product-name">Scribe</span>
        </a>
        <div className="sidebar-product"><span>Model Evaluation</span></div>
        <nav aria-label="Main navigation">
          <button className={tab !== "overview" ? "active" : ""} onClick={() => setTab("compare")}><DesignIcon name="compare"/>Compare</button>
          <button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}><DesignIcon name="analytics"/>Analytics</button>
          <button onClick={() => setModal("library")}><DesignIcon name="library"/>Library</button>
          <button onClick={() => setModal("history")}><DesignIcon name="history"/>History</button>
        </nav>
        <div className="sidebar-footer">
          <button onClick={() => { setManageTarget(null); setManageRole(""); setManagerReturn(""); setModal("models"); }}><DesignIcon name="settings"/>Saved Models</button>
          <button onClick={() => setModal("connections")}><PlugZap size={16}/>Connections</button>
          <span className="b-muted">Local Workspace</span>
        </div>
      </aside>
      <main className="bench-main">
      <header className="bench-nav">
        <strong>{tab === "overview" ? "Workspace Analytics" : "Model Comparison"}</strong>
        <div className="nav-utilities">
          <span className="local-indicator"><i className={data.system.worker_status === "online" ? "online" : ""}/>{data.system.worker_status === "online" ? "Ready" : "Service Offline"}</span>
          <ThemeSwitcher />
        </div>
      </header>
      {tab === "overview" ? <GlobalAnalytics onOpenRun={(id) => { setRunId(id); setTab("compare"); }} /> : <>
      <section className="workspace-toolbar">
        <div className="experiment-title">
          {run ? (
            <>
              <strong>{run.name}</strong>
              <Badge
                tone={
                  run.status === "complete"
                    ? "teal"
                    : active
                      ? "blue"
                      : "neutral"
                }
              >
                {label(run.status)}
              </Badge>
            </>
          ) : (
            <>
              <input
                value={config.name}
                onChange={(e) => patch("name", e.target.value)}
                aria-label="Comparison Name"
              />
              <Badge>Draft</Badge>
            </>
          )}
          <span className="b-muted">
            {run ? "Saved setup" : "Saved on this computer"}
          </span>
        </div>
        <div className="workspace-tabs">
          <button
            className={tab === "compare" ? "active" : ""}
            onClick={() => setTab("compare")}
          >
            <Layers3 size={14} />
            Compare
          </button>
          <button
            className={tab === "analytics" ? "active" : ""}
            onClick={() => setTab("analytics")}
          >
            <BarChart3 size={14} />
            Run Analytics
          </button>
          <button
            className={tab === "sources" ? "active" : ""}
            onClick={() => setTab("sources")}
          >
            <FileText size={14} />
            Source & Realism
          </button>
        </div>
        <div className="toolbar-actions">
          {run ? (
            <>
              <button
                className="b-button"
                disabled={busy}
                onClick={copyComparison}
              >
                <Copy size={14} />
                Copy Comparison
              </button>
              <a
                className="b-button"
                href={"/api/bench/runs/" + runId + "/export"}
                download
              >
                <Download size={14} />
                Export
              </a>
              {active ? (
                <button
                  className="b-button danger"
                  disabled={busy || run.status === "stopping"}
                  onClick={() => runAction("stop")}
                >
                  <Pause size={14} />
                  {run.status === "stopping" ? "Draining…" : "Stop"}
                </button>
              ) : run.status === "interrupted" ? (
                <button
                  className="b-button primary"
                  onClick={() => runAction("resume")}
                >
                  Resume
                </button>
              ) : (
                <button className="b-button primary" onClick={newComparison}>
                  <Plus size={14} />
                  New Comparison
                </button>
              )}
            </>
          ) : (
            <>
              {previousDraft && <button className="b-button subtle" onClick={() => { const prior = previousDraft; setPreviousDraft(structuredClone(config)); setConfig(prior); setCopiedFrom(""); setModal("setup"); }}>Restore Draft</button>}
              <button className="b-button" onClick={() => setModal("setup")}><Settings2 size={14}/>Edit Setup</button>
              <button className="b-button" onClick={() => setModal("settings")}>
                <SlidersHorizontal size={14} />
                Run Settings
              </button>
              <button
                className="b-button primary"
                disabled={busy || preflight.length > 0}
                onClick={start}
              >
                <Play size={14} />
                {busy ? "Starting…" : "Run Comparison"}
              </button>
            </>
          )}
        </div>
      </section>
      <ErrorBox error={error || runQ.error || suiteQ.error || promptQ.error} />
      {run?.halt_reason && <div className="workspace-notice"><Badge tone="warning">Evaluation Incomplete</Badge><span>{run.halt_reason}</span>{!active && <button className="b-link" onClick={() => setModal("reevaluate")}>Review Evaluation Settings</button>}</div>}
      {promptQ.error && !runId && (
        <button className="b-button" onClick={() => promptQ.refetch()}>
          Retry Prompt
        </button>
      )}
      {suiteQ.error && !runId && (
        <button className="b-button" onClick={() => suiteQ.refetch()}>
          Retry Scenarios
        </button>
      )}
      {!run &&
        models.some(
          (m) =>
            m.demonstration ||
            data.connections.find((c) => c.id === m.connection_id)?.provider ===
              "demo",
        ) && (
          <div className="workspace-notice demo">
            Includes a scripted local demo model. Its outputs and timing are
            demonstration evidence; real provider calls still use the configured
            spending cap.
          </div>
        )}
      {run?.manifest?.demonstration && (
        <div className="workspace-notice demo">
          Includes scripted demonstration components. Demo outputs and timing
          are local measurements; real provider usage is measured and billed
          separately. Mixed results do not establish provider capacity.
        </div>
      )}
      {active && (
        <div className="live-progress">
          <span className="pulse-dot" />
          <strong>{label(run.phase || run.status)}</strong>
          <span>
            {run.progress?.completed_conversations || 0} /{" "}
            {run.progress?.total_conversations || "—"} conversations
          </span>
          <span>{run.progress?.active_conversations || 0} active</span>
          <span>{run.progress?.inflight_requests || 0} in flight</span>
          <span>Estimated spend {money(run.progress?.spend_usd)}</span>
          {waiting && <span>{waiting}</span>}
          <div>
            <i
              style={{
                width:
                  Math.min(
                    100,
                    ((run.progress?.completed_conversations || 0) /
                      Math.max(1, run.progress?.total_conversations || 1)) *
                      100,
                  ) + "%",
              }}
            />
          </div>
        </div>
      )}
      <section className="scenario-strip">
        <div className="scenario-selector">
          <span className="b-overline">SHARED SCENARIO</span>
          <select
            aria-label="Shared Scenario"
            value={scenario?.id || ""}
            onChange={(e) => {
              setCaseIndex(cases.findIndex((c) => c.id === e.target.value));
              setSelectedEvidence(null);
            }}
          >
            {!cases.length && <option value="">Choose a scenario set</option>}
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <div className="case-paging">
            <button
              className="b-icon"
              aria-label="Previous Scenario"
              disabled={caseIndex === 0}
              onClick={() => setCaseIndex((i) => i - 1)}
            >
              <ArrowLeft size={14} />
            </button>
            <span>
              {cases.length ? caseIndex + 1 : 0} / {cases.length}
            </span>
            <button
              className="b-icon"
              aria-label="Next Scenario"
              disabled={caseIndex >= cases.length - 1}
              onClick={() => setCaseIndex((i) => i + 1)}
            >
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
        <Badge tone="teal">
          {mode === "replay"
            ? "Identical replay input"
            : "Shared persona · adaptive"}
        </Badge>
        {scenario && (
          <>
            <Badge>{scenario.language?.toUpperCase()}</Badge>
            <Badge
              tone={scenario.review_status === "reviewed" ? "teal" : "warning"}
            >
              {scenario.review_status === "reviewed"
                ? "Scenario reviewed"
                : "Draft scenario"}
            </Badge>
          </>
        )}
        <button
          className="b-link"
          disabled={!!suite && !suiteReady}
          onClick={() => setScenarioOpen(!scenarioOpen)}
        >
          {scenarioOpen
            ? "Hide shared context"
            : "Persona & Expected Behavior"}
          <ChevronDown size={14} />
        </button>
        {!run && (
          <>
            <select
              className="suite-selector"
              aria-label="Scenario Set"
              value={config.suite_id}
              onChange={(e) => {
                const chosen = selectableSuites.find(
                  (s) => s.id === e.target.value,
                );
                const matchingPromptId = chosen
                  ? promptForSuite(chosen, selectablePrompts, config.prompt_id)
                  : null;
                if (
                  chosen &&
                  matchingPromptId &&
                  matchingPromptId !== config.prompt_id
                )
                  toast.info(
                    "Selected matching prompt: " +
                      selectablePrompts.find((p) => p.id === matchingPromptId)
                        ?.name,
                  );
                if (chosen && !matchingPromptId)
                  toast.info(
                    "Choose a prompt matching this scenario set’s campaign and language.",
                  );
                setConfig((c) => ({
                  ...c,
                  prompt_id: matchingPromptId || c.prompt_id,
                  suite_id: e.target.value,
                  clock_date: chosen?.clock_date || c.clock_date,
                  timezone: chosen?.timezone || c.timezone,
                }));
                setCaseIndex(0);
              }}
            >
              <option value="">Select scenario set</option>
              {selectableSuites
                .filter(
                  (s) =>
                    s.id === config.suite_id ||
                    (s.status !== "superseded" &&
                      (showDraftSources ||
                        (s.source_kind === "production-transcript" &&
                          s.qualification?.eligible === true))),
                )
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · v{s.version}
                    {s.source_kind === "production-transcript"
                      ? s.qualification?.eligible
                        ? " · source eligible"
                        : " · threshold unverified"
                      : " · draft / demo"}
                  </option>
                ))}
            </select>
            <button
              className="b-icon"
              aria-label="Edit Scenarios"
              disabled={!!config.suite_id && !suiteReady}
              onClick={() => {
                setNewSuite(!suite);
                setModal("scenarios");
              }}
            >
              <Settings2 size={15} />
            </button>
            <button
              className="b-icon"
              aria-label="New Scenario Set"
              onClick={() => {
                setNewSuite(true);
                setModal("scenarios");
              }}
            >
              <Plus size={15} />
            </button>
          </>
        )}
      </section>
      {!run && (
        <div className="source-filter-strip">
          <label>
            <input
              type="checkbox"
              checked={showDraftSources}
              onChange={(e) => {
                setShowDraftSources(e.target.checked);
                if (!e.target.checked)
                  setConfig((c) => ({
                    ...c,
                    prompt_id:
                      data.prompts.find((p) => p.id === c.prompt_id)
                        ?.qualification?.eligible === true
                        ? c.prompt_id
                        : "",
                    suite_id:
                      selectableSuites.find((s) => s.id === c.suite_id)
                        ?.qualification?.eligible === true
                        ? c.suite_id
                        : "",
                  }));
              }}
            />{" "}
            Inspect unqualified sources, drafts & demos
          </label>
          <span>
            Default sources meet the verified outbound-call threshold. This does
            not qualify policy expectations or human realism.
          </span>
          {suite?.source_kind === "production-transcript" && (
            <Badge tone={suite.qualification?.eligible ? "teal" : "warning"}>
              {suite.qualification?.eligible
                ? `${suite.qualification.distinct_outgoing_uuid_lower_bound}+ verified outbound calls · `
                : "Outbound-call threshold unverified · "}
              {suite.ready_case_count ?? suite.case_count ?? cases.length} ready
              source cases
            </Badge>
          )}
        </div>
      )}
      {suiteLoading && (
        <Loading text="Loading the complete source history and frozen prompt…" />
      )}
      {scenarioOpen && scenario && suiteReady && (
        <section className="shared-context">
          <div>
            <span className="b-overline">PERSONA & SITUATION</span>
            <p dir="auto">
              {scenario.persona || scenario.scenario || scenario.name}
            </p>
            <small>
              Frozen date: {currentConfig.clock_date} · {currentConfig.timezone}
            </small>
          </div>
          <div>
            <span className="b-overline">EXPECTED BEHAVIOR</span>
            <div className="context-expectations" dir="auto">
              <FactList value={scenario.expected_behavior} />
            </div>
            {scenario.critical_rules?.map((r: string) => (
              <p key={r} className="critical-rule">
                {r}
              </p>
            ))}
          </div>
          <div>
            <span className="b-overline">SHARED FACTS</span>
            <FactList value={scenario.facts || {}} />
            <details className="raw-facts">
              <summary>Raw source variables</summary>
              <pre dir="auto">
                {JSON.stringify(scenario.facts || {}, null, 2)}
              </pre>
            </details>
          </div>
        </section>
      )}
      {!run && preflight.length > 0 && (
        <div className="preflight-bar">
          <ShieldCheck size={15} />
          <strong>Complete the setup</strong>
          <span>{preflight.join(" · ")}</span>
          {!prompt && (
            <button className="b-link" onClick={() => setModal("library")}>
              Open Library <ArrowRight size={13} />
            </button>
          )}
        </div>
      )}
      {!run && <section className="evaluation-summary">
        <div><ShieldCheck size={16}/><strong>Evaluation</strong><span>{judge?.name || "Choose a quality judge"}</span></div>
        <p className="b-muted">{mode === "replay" ? "Every model receives the same recorded customer turns." : "The simulator plays the customer. The realism judge checks the conversation against real transcripts."} {judge ? "Judgments include quotes from the conversation." : "You can compare responses, speed, and cost. Choose a judge to evaluate quality."}</p>
        <button className="b-button" onClick={() => setModal("setup")}>Choose Models & Judges</button>
      </section>}
      {tab === "analytics" ? (
        <Analytics
          run={run}
          onInspect={(id, mid) => {
            setCaseIndex(Math.max(0, cases.findIndex((c) => c.id === id)));
            setReviewModel(mid);
            setTab("compare");
            setJudgeOpen(true);
          }}
        />
      ) : tab === "sources" ? (
        suiteLoading ? (
          <Loading text="Loading source evidence…" />
        ) : (
          <SourceView
            scenario={scenario}
            convs={caseConvs}
            models={models}
            mode={mode}
          />
        )
      ) : (
        <>
          <div
            className={
              "comparison-layout " +
              (!promptVisible ? "prompt-hidden" : "") +
              (fullPrompt ? " prompt-full" : "")
            }
          >
            <aside className="prompt-pane">
              <div className="pane-heading">
                <span>
                  <FileText size={15} />
                  <strong>System Prompt</strong>
                </span>
                <div>
                  <button
                    className="b-icon"
                    aria-label={
                      fullPrompt
                        ? "Restore comparison layout"
                        : "Expand Prompt"
                    }
                    onClick={() => setFullPrompt(!fullPrompt)}
                  >
                    {fullPrompt ? (
                      <Minimize2 size={14} />
                    ) : (
                      <Maximize2 size={14} />
                    )}
                  </button>
                  <button
                    className="b-icon"
                    aria-label="Hide Prompt"
                    onClick={() => {
                      setPromptVisible(false);
                      setFullPrompt(false);
                    }}
                  >
                    <PanelLeftClose size={14} />
                  </button>
                </div>
              </div>
              {run ? (
                <div className="prompt-selection">
                  <strong>{prompt?.name}</strong>
                  <Badge>v{prompt?.version}</Badge>
                </div>
              ) : (
                <div className="prompt-selection">
                  <select
                    aria-label="Prompt Version"
                    value={config.prompt_id}
                    onChange={(e) => patch("prompt_id", e.target.value)}
                  >
                    <option value="">Choose a prompt</option>
                    {selectablePrompts
                      .filter(
                        (p) =>
                          p.id === config.prompt_id ||
                          (p.status !== "superseded" &&
                            (showDraftSources ||
                              (p.source_kind === "production-master-template" &&
                                p.qualification?.eligible === true))),
                      )
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} · v{p.version}
                          {p.source_kind === "production-master-template"
                            ? p.qualification?.eligible
                              ? " · source eligible"
                              : " · threshold unverified"
                            : " · draft / demo"}
                        </option>
                      ))}
                  </select>
                  <button
                    className="b-icon"
                    aria-label="New Prompt"
                    onClick={() => {
                      setNewPrompt(true);
                      setModal("prompt");
                    }}
                  >
                    <Plus size={14} />
                  </button>
                </div>
              )}
              <div className="prompt-mode-tabs">
                <button
                  className={promptTab === "source" ? "active" : ""}
                  onClick={() => setPromptTab("source")}
                >
                  Source
                </button>
                <button
                  className={promptTab === "rendered" ? "active" : ""}
                  onClick={() => setPromptTab("rendered")}
                >
                  Rendered
                </button>
                <span>
                  {promptLoading && promptTab === "source"
                    ? "Loading…"
                    : `${sourceText?.length?.toLocaleString() || 0} chars`}
                </span>
              </div>
              <div className="b-search prompt-search">
                <Search size={13} />
                <input
                  value={promptSearch}
                  onChange={(e) => setPromptSearch(e.target.value)}
                  placeholder="Find in prompt"
                  aria-label="Search complete prompt"
                />
                {promptSearch && (
                  <span>
                    {sourceText?.toLowerCase().split(promptSearch.toLowerCase())
                      .length - 1 || 0}
                  </span>
                )}
              </div>
              <div className="prompt-reading" dir="auto">
                {(promptTab === "source" ? promptLoading : suiteLoading) ? (
                  <Loading
                    text={
                      promptTab === "source"
                        ? "Loading the complete prompt…"
                        : "Loading the complete rendered request…"
                    }
                  />
                ) : sourceText ? (
                  <MarkedText
                    text={sourceText}
                    query={promptSearch}
                    insensitive
                  />
                ) : (
                  <Empty
                    title={
                      promptTab === "rendered"
                        ? "No rendered snapshot yet"
                        : "Your complete prompt lives here"
                    }
                  >
                    {promptTab === "rendered"
                      ? "A saved comparison or native library import supplies the exact rendered text. No client-side substitution is presented as a frozen snapshot."
                      : "Import a version from Scribe, or write a new prompt."}
                  </Empty>
                )}
              </div>
              <div className="prompt-footer">
                <span>{completeness}</span>
                <div>
                  <button
                    className="b-icon"
                    disabled={!sourceText}
                    aria-label="Copy Prompt"
                    onClick={() => {
                      navigator.clipboard.writeText(sourceText || "");
                      toast.success("Complete prompt copied");
                    }}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    className="b-button"
                    disabled={!prompt || !promptReady || !!run}
                    onClick={() => {
                      setNewPrompt(false);
                      setModal("prompt");
                    }}
                  >
                    Edit Version
                  </button>
                </div>
              </div>
            </aside>
            <section ref={comparisonCanvas} className="comparison-canvas" style={gridStyle} tabIndex={0} aria-label="Model comparison columns">
              <div className="canvas-controls">
                <div>
                  {!promptVisible && (
                    <button
                      className="b-button subtle"
                      onClick={() => setPromptVisible(true)}
                    >
                      <PanelLeftOpen size={14} />
                      Prompt
                    </button>
                  )}
                  <span className="b-overline">
                    {focusModel
                      ? "FOCUSED RESPONSE"
                      : models.length === 1
                        ? "MODEL EVALUATION"
                        : "MODEL COMPARISON"}
                  </span>
                  {mode === "adaptive" && (
                    <Badge tone="warning">
                      Later customer turns may differ
                    </Badge>
                  )}
                </div>
                <div>
                  {!focusModel && displayedModels.length > 4 && <label className="model-jump">Jump to Model<select aria-label="Jump to comparison model" value="" onChange={event => {
                    const canvas = comparisonCanvas.current;
                    const index = Number(event.target.value);
                    const header = canvas?.querySelector<HTMLElement>(`[data-model-column="${index}"]`);
                    const pinned = canvas?.querySelector<HTMLElement>('[data-model-column="0"]');
                    if (canvas && header) {
                      const delta = header.getBoundingClientRect().left - canvas.getBoundingClientRect().left - (index ? pinned?.offsetWidth || 0 : 0);
                      if (index === 0) canvas.scrollTo({ left: 0, behavior: "instant" });
                      else canvas.scrollBy({ left: delta, behavior: "instant" });
                      header.focus({ preventScroll: true });
                    }
                  }}><option value="" disabled>{displayedModels.length} model columns</option>{displayedModels.map((model, index) => <option key={index} value={index}>{index === 0 ? "Baseline" : `Candidate ${index}`} · {model?.name || "Choose Model"}</option>)}</select></label>}
                  {focusModel && (
                    <button
                      className="b-link"
                      onClick={() => setFocusModel("")}
                    >
                      Show All Models
                    </button>
                  )}
                  {!run && (
                    <button
                      className="b-button subtle"
                      disabled={draftSlots.length >= MAX_COMPARISON_MODELS}
                      title={
                        draftSlots.length >= MAX_COMPARISON_MODELS
                          ? `Maximum ${MAX_COMPARISON_MODELS} models reached, including the baseline. Remove a model to add another.`
                          : "Choose another saved model profile"
                      }
                      aria-label={
                        draftSlots.length >= MAX_COMPARISON_MODELS
                          ? `Add Model, ${MAX_COMPARISON_MODELS} of ${MAX_COMPARISON_MODELS} models. Maximum reached; remove a model to add another.`
                          : `Add Model, ${draftSlots.length} of ${MAX_COMPARISON_MODELS} model slots selected`
                      }
                      onClick={() => {
                        setManageTarget(
                          !config.baseline_id ? 0 : config.candidate_ids.includes("") ? config.candidate_ids.indexOf("") + 1 : config.candidate_ids.length + 1,
                        );
                        setModal("select-model");
                      }}
                    >
                      <Plus size={14} />
                      Add Model · {draftSlots.length}/{MAX_COMPARISON_MODELS}
                    </button>
                  )}
                </div>
              </div>
              <div className="model-grid model-headers">
                {displayedModels.map((m, index) => {
                  const id = m?.id || "";
                  const result = run?.results?.find(
                    (r: Obj) => r.model_id === id,
                  );
                  const c = convFor(id);
                  return (
                    <div
                      className={
                        "model-header " +
                        ((id ? id === baseline : index === 0) ? "baseline" : "")
                      }
                      key={id || index}
                      data-model-column={index}
                      tabIndex={-1}
                    >
                      <div className="model-topline">
                        <Badge
                          tone={
                            (id ? id === baseline : index === 0)
                              ? "teal"
                              : "neutral"
                          }
                        >
                          {(id ? id === baseline : index === 0)
                            ? "Baseline"
                            : "Candidate " +
                              (run && m
                                ? models.findIndex((x) => x.id === m.id)
                                : index)}
                        </Badge>
                        {(m?.demonstration ||
                          run?.manifest?.demonstration_model_ids?.includes(
                            id,
                          ) ||
                          data.connections.find(
                            (connection) => connection.id === m?.connection_id,
                          )?.provider === "demo") && (
                          <Badge>Scripted local demo</Badge>
                        )}
                        <div className="model-actions">
                          {m && run && (
                            <button
                              className="b-icon"
                              aria-label={"Focus " + m.name}
                              onClick={() =>
                                setFocusModel(focusModel ? "" : m.id)
                              }
                            >
                              <Maximize2 size={13} />
                            </button>
                          )}
                          {!run && index > 0 && m && (
                            <button
                              className="b-link"
                              onClick={() =>
                                setConfig((c) => {
                                  const candidates = [...c.candidate_ids];
                                  candidates[index - 1] = c.baseline_id;
                                  return {
                                    ...c,
                                    baseline_id: m.id,
                                    candidate_ids: candidates,
                                  };
                                })
                              }
                            >
                              Make Baseline
                            </button>
                          )}
                          {!run && index > 0 && (
                            <button
                              className="b-icon"
                              aria-label="Remove Candidate"
                              onClick={() =>
                                patch(
                                  "candidate_ids",
                                  config.candidate_ids.filter(
                                    (_: string, i: number) => i !== index - 1,
                                  ),
                                )
                              }
                            >
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                      {run ? (
                        <>
                          <strong className="model-name">{m?.name}</strong>
                          <code className="profile-exact">{m?.model_id}</code>
                        </>
                      ) : (
                        <ProfilePicker
                          data={data}
                          value={id}
                          excludeIds={models.map((model) => model.id)}
                          onChange={(id) => updateModel(index, id)}
                          onManage={() => {
                            manageProfile("target", index, "");
                          }}
                        />
                      )}
                      {m && (
                        <div className="model-settings-summary">
                          <span>
                            {temperatureLabel(m, run?.manifest?.connections?.[m.connection_id]?.provider || data.connections.find(c => c.id === m.connection_id)?.provider)}
                          </span>
                          <span>
                            {reasoningLabel(m)}
                          </span>
                          <span>{number(m.max_output_tokens)} output</span>
                        </div>
                      )}
                      <div className="model-metrics">
                        <span className={outcome(c) === "Pass" ? "teal" : ""}>
                          {run ? outcome(c) : "Ready to set up"}
                        </span>
                        <span>
                          {duration(
                            c?.turns?.[0]?.latency_ms || result?.p50_ms,
                          )}
                        </span>
                        {c?.status && <small>{label(c.status)}</small>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="aligned-transcript">
                {suiteLoading ? (
                  <Loading text="Loading shared checkpoints…" />
                ) : !scenario ? (
                  <Empty title="One scenario. Every model. Side by side.">
                    Choose a prompt and scenario set, then select your baseline
                    and up to {MAX_COMPARISON_MODELS - 1} candidate profiles. A single-model run is
                    also supported. All outputs will appear here with the same
                    shared customer input.
                  </Empty>
                ) : (
                  Array.from({ length: maxTurns }, (_, i) => {
                    const cp = checkpoints[i];
                    const sharedUser =
                      cp?.user_turn ||
                      cp?.user ||
                      caseConvs[0]?.turns?.[i]?.user;
                    return (
                      <div className="comparison-turn" key={cp?.id || i}>
                        {mode === "replay" || i === 0 ? (
                          <div className="shared-customer">
                            <div>
                              <span className="b-overline">
                                CUSTOMER · {i + 1}
                              </span>
                              <Badge>
                                {cp?.reference_history_source ||
                                  "Frozen shared input"}
                              </Badge>
                              {cp?.history?.length > 0 && (
                                <details>
                                  <summary>
                                    {cp.history.length} prior messages
                                  </summary>
                                  {cp.history.map((h: Obj, j: number) => (
                                    <p key={j} dir="auto">
                                      <strong>{label(h.role)}:</strong>{" "}
                                      {h.content}
                                    </p>
                                  ))}
                                </details>
                              )}
                            </div>
                            <p dir="auto">
                              {sharedUser || "No customer message saved."}
                            </p>
                          </div>
                        ) : (
                          <div className="divergence-label">
                            Adaptive dialogue · each customer follows the
                            response in its column
                          </div>
                        )}
                        <div className="model-grid output-row">
                          {displayedModels.map((m, mi) => {
                            const c = m ? convFor(m.id) : undefined;
                            const t = c?.turns?.[i];
                            const awaitingTurn =
                              (c?.status === "running" && active) ||
                              (!c &&
                                ["queued", "running"].includes(run?.status));
                            const selected =
                              selectedEvidence?.model === m?.id &&
                              selectedEvidence?.turn === (t?.index || i + 1);
                            return (
                              <article
                                id={
                                  m
                                    ? "response-" +
                                      m.id +
                                      "-" +
                                      (t?.index || i + 1)
                                    : undefined
                                }
                                className={
                                  "model-output " +
                                  ((m ? m.id === baseline : mi === 0)
                                    ? "baseline"
                                    : "") +
                                  (selected ? " evidence-selected" : "")
                                }
                                key={m?.id || mi}
                              >
                                {mode === "adaptive" &&
                                  i > 0 &&
                                  (t?.user || awaitingTurn) && (
                                    <p className="adaptive-customer" dir="auto">
                                      {t?.user || "Customer turn pending"}
                                    </p>
                                  )}
                                {t ? (
                                  <>
                                    <div className="response-text" dir="auto">
                                      <MarkedText
                                        text={t.assistant}
                                        query={
                                          selected
                                            ? selectedEvidence?.quote || ""
                                            : ""
                                        }
                                      />
                                    </div>
                                    <footer>
                                      <span>
                                        {duration(t.latency_ms)}
                                        {t.ttft_ms != null
                                          ? " · first token " +
                                            duration(t.ttft_ms)
                                          : ""}
                                      </span>
                                      <button
                                        className="b-link"
                                        onClick={() => {
                                          setReviewModel(m!.id);
                                          setJudgeOpen(true);
                                          setSelectedEvidence({
                                            model: m!.id,
                                            turn: t.index || i + 1,
                                            quote: "",
                                          });
                                        }}
                                      >
                                        Review Response
                                      </button>
                                    </footer>
                                  </>
                                ) : c?.status === "complete" ? (
                                  <div className="response-pending">
                                    <strong>
                                      Conversation ended after{" "}
                                      {c.turns?.length || 0}{" "}
                                      {c.turns?.length === 1 ? "turn" : "turns"}
                                      .
                                    </strong>
                                    <p>
                                      {c.ending?.reason ===
                                      "customer_turn_limit"
                                        ? "Configured turn limit reached."
                                        : c.ending?.reason ===
                                            "customer_simulator_end"
                                          ? "The customer simulator signaled an end."
                                          : mode === "replay"
                                            ? "All recorded checkpoints completed."
                                            : "No further response is expected."}
                                    </p>
                                  </div>
                                ) : c?.failure_reason ? (
                                  <div className="response-error">
                                    <Badge tone="error">
                                      {label(c.status)}
                                    </Badge>
                                    <p>{c.failure_reason}</p>
                                  </div>
                                ) : run ? (
                                  <div className="response-pending">
                                    {awaitingTurn
                                      ? "Waiting for this response…"
                                      : "No response recorded for this checkpoint."}
                                  </div>
                                ) : (
                                  <div className="response-placeholder">
                                    <span>
                                      {m
                                        ? "Ready for " + m.name
                                        : "Select a model profile"}
                                    </span>
                                    <div />
                                    <div />
                                    <div />
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
          {run && <section className={"judge-dock " + (!judgeOpen ? "collapsed" : "")}>
            <div className="judge-heading">
              <button
                className="b-link"
                onClick={() => setJudgeOpen(!judgeOpen)}
              >
                <ShieldCheck size={16} />
                <strong>Review Judgments</strong>
                <ChevronDown size={14} />
              </button>
              <span>{judge?.name || "No judge selected"}</span>
              {(overlap || providerOverlap) && (
                <Badge tone="warning">
                  {overlap
                    ? "Judge Also Under Test"
                    : "Same Provider as Target"}
                </Badge>
              )}
              <span className="b-muted">{judge?.model_id}</span>
              {run && !active && (
                <button
                  className="b-button subtle"
                  onClick={() => setModal("reevaluate")}
                  disabled={busy || run.status === "interrupted"}
                  title={run.status === "interrupted" ? "Resume the interrupted run before starting a new evaluation." : "Choose the evaluator and spending limit for a new judgment revision."}
                >
                  <RotateCcw size={13} />
                  Re-evaluate
                </button>
              )}
            </div>
            {judgeOpen && (
              <JudgeDock
                models={models}
                convs={caseConvs}
                selectedModel={reviewModel || models[0]?.id || ""}
                onSelect={setReviewModel}
                onEvidence={(mid, turn, quote) => {
                  setSelectedEvidence({ model: mid, turn, quote });
                  setFocusModel("");
                  setTimeout(
                    () =>
                      document
                        .getElementById("response-" + mid + "-" + turn)
                        ?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        }),
                    40,
                  );
                }}
                run={run}
                onRefresh={refresh}
              />
            )}
          </section>}
        </>
      )}
      <footer className="workspace-footer">
        <span>ClearGrid Scribe · Local Workspace</span>
        <span>
          Text model evaluation · {run ? "Saved comparison" : "Draft setup"} ·{" "}
          {currentConfig.clock_date}
        </span>
        <span>
          {suite?.name || "No scenario set"}{" "}
          {suite ? "· v" + suite.version : ""}
        </span>
      </footer>
      </>}
      </main></div>
      {modal === "reevaluate" && run && <Reevaluate run={run} data={data} onClose={() => setModal("")} onComplete={refresh} />}
      {modal === "setup" && <ComparisonSetup data={data} config={config} patch={patch} onManage={manageProfile} onClose={() => setModal("")} copiedFrom={copiedFrom} issues={preflight} warnings={draftWarnings} />}
      {modal === "connections" && (
        <Connections
          data={data}
          onClose={() => setModal("")}
          onRefresh={refresh}
        />
      )}{" "}
      {modal === "select-model" && (
        <ProfileSelectDialog
          data={data}
          excludeIds={models.map((m) => m.id)}
          onChange={(id) => updateModel(manageTarget ?? 0, id)}
          onManage={() => setModal("models")}
          onClose={() => setModal("")}
        />
      )}
      {modal === "models" && (
        <ModelManager
          data={data}
          initialId={manageRole ? config[manageRole] : manageTarget == null ? "" : [config.baseline_id, ...config.candidate_ids][manageTarget]}
          onClose={() => { setModal(managerReturn); setManageTarget(null); setManageRole(""); setManagerReturn(""); }}
          onRefresh={refresh}
          onSelect={manageRole ? (id) => patch(manageRole, id) : manageTarget == null ? undefined : (id) => updateModel(manageTarget, id)}
        />
      )}
      {modal === "library" && (
        <LibraryImport
          onClose={() => setModal("")}
          onImported={(p, s, clock, tz) => {
            setPreviousDraft(structuredClone(config));
            setShowDraftSources(true);
            refresh();
            draft();
            setConfig((c) => ({
              ...c,
              prompt_id: p,
              suite_id: s,
              clock_date: clock || c.clock_date,
              timezone: tz || c.timezone,
            }));
            setCaseIndex(0);
          }}
        />
      )}
      {modal === "prompt" && (newPrompt || promptReady) && (
        <PromptEditor
          prompt={newPrompt ? undefined : prompt}
          onClose={() => setModal("")}
          onSaved={(id) => {
            setShowDraftSources(true);
            refresh();
            patch("prompt_id", id);
          }}
        />
      )}
      {modal === "scenarios" && (newSuite || suiteReady) && (
        <ScenarioEditor
          suite={newSuite ? undefined : suite}
          prompt={prompt}
          data={data}
          onClose={() => setModal("")}
          onSaved={(id) => {
            setShowDraftSources(true);
            refresh();
            patch("suite_id", id);
            setCaseIndex(0);
          }}
        />
      )}
      {modal === "settings" && (
        <RunSettings
          config={config}
          patch={patch}
          data={data}
          onClose={() => setModal("")}
          preflight={preflight}
        />
      )}
      {modal === "history" && (
        <HistoryView
          runs={data.runs}
          onClose={() => setModal("")}
          onOpen={(id) => {
            setRunId(id);
            setModal("");
            setTab("compare");
          }}
          onNew={() => {
            newComparison();
            setModal("");
          }}
        />
      )}
    </div>
  );
}
function MarkedText({
  text,
  query,
  insensitive = false,
}: {
  text: string;
  query: string;
  insensitive?: boolean;
}) {
  if (!query) return <>{text}</>;
  const needle = insensitive ? query.toLowerCase() : query,
    hay = insensitive ? text.toLowerCase() : text;
  const parts: React.ReactNode[] = [];
  let cursor = 0,
    at = hay.indexOf(needle);
  while (at !== -1) {
    parts.push(
      text.slice(cursor, at),
      <mark key={at}>{text.slice(at, at + query.length)}</mark>,
    );
    cursor = at + query.length;
    at = hay.indexOf(needle, cursor);
  }
  parts.push(text.slice(cursor));
  return <>{parts}</>;
}
function JudgeDock({
  models,
  convs,
  selectedModel,
  onSelect,
  onEvidence,
  run,
  onRefresh,
}: {
  models: Model[];
  convs: Obj[];
  selectedModel: string;
  onSelect: (id: string) => void;
  onEvidence: (id: string, t: number, q: string) => void;
  run: Obj | null;
  onRefresh: () => void;
}) {
  const c = convs.find((c) => c.model_id === selectedModel) || convs[0];
  const e = evaluation(c);
  const [note, setNote] = useState("");
  const [decision, setDecision] = useState("agree");
  const [correct, setCorrect] = useState("pass");
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [ruleSelected, setRuleSelected] = useState("");
  const criteria: Obj[] = (e.checkpoint_evaluations || []).flatMap((cp: Obj) =>
    (cp.criteria || []).map((r: Obj) => ({
      ...r,
      checkpoint_id: cp.checkpoint_id,
      key: (cp.checkpoint_id || "adaptive") + ":" + r.rule_id,
    })),
  );
  const selectedRule = criteria.find((r) => r.key === ruleSelected);
  useEffect(() => {
    setNote("");
    setError(null);
    setDecision("agree");
  }, [c?.id]);
  return (
    <>
      <CheckpointMatrix
        models={models}
        convs={convs}
        onChoose={(mid, key) => {
          onSelect(mid);
          setRuleSelected(key);
        }}
      />
      <div className="judge-body">
        <div className="criterion-matrix">
          <div className="b-overline">SCENARIO OUTCOME</div>
          {models.map((m) => {
            const x = convs.find((c) => c.model_id === m.id),
              v = evaluation(x);
            return (
              <button
                className={c?.model_id === m.id ? "selected" : ""}
                key={m.id}
                onClick={() => onSelect(m.id)}
              >
                <span>{m.name}</span>
                <Badge
                  tone={
                    v.critical
                      ? "error"
                      : v.status !== "evaluated"
                        ? checkpointFindings(x).graded > 0
                          ? "warning"
                          : "neutral"
                        : v.correct
                          ? "teal"
                          : "error"
                  }
                >
                  {outcome(x)}
                </Badge>
                {v.human_review && <small>Human Reviewed</small>}
              </button>
            );
          })}
          {!models.length && (
            <p className="b-muted">
              Select model profiles to compare criterion outcomes.
            </p>
          )}
          <p className="b-muted">
            Literal checks and model judgments remain separate. Ungraded is
            never a pass.
          </p>
        </div>
        <div className="judge-evidence">
          <div className="section-toolbar">
            <strong>
              {models.find((m) => m.id === c?.model_id)?.name ||
                "Evaluation evidence"}
            </strong>
            {c && (
              <Badge>
                {e.human_review ? "Human-reviewed outcome" : "Model judgment"}
              </Badge>
            )}
          </div>
          {selectedRule && (
            <div className="selected-criterion">
              <Badge
                tone={
                  selectedRule.outcome === "pass"
                    ? "teal"
                    : selectedRule.outcome === "fail"
                      ? "error"
                      : "neutral"
                }
              >
                {label(selectedRule.outcome)}
              </Badge>
              <strong>{selectedRule.text}</strong>
              <p>{selectedRule.reason}</p>
              {(selectedRule.evidence || []).map((ev: Obj, i: number) => (
                <button
                  className="evidence-quote"
                  key={i}
                  onClick={() => onEvidence(c.model_id, ev.turn, ev.quote)}
                >
                  <span>Turn {ev.turn} · Exact evidence</span>
                  <blockquote dir="auto">“{ev.quote}”</blockquote>
                </button>
              ))}
              <button className="b-link" onClick={() => setRuleSelected("")}>
                Show All Evidence
              </button>
            </div>
          )}
          {c &&
            (checkpointFindings(c).graded > 0 ||
              checkpointFindings(c).failed > 0) &&
            e.status !== "evaluated" && (
              <div className="workspace-notice">
                Partially evaluated: {checkpointFindings(c).graded} checkpoints
                graded; {checkpointFindings(c).ungraded} remain ungraded.{" "}
                {checkpointFindings(c).failed} known failed checkpoints,
                including {checkpointFindings(c).critical} critical. Overall
                pass rate is unavailable.
              </div>
            )}
          <p dir="auto">
            {e.rationale ||
              e.reason ||
              "The selected judge evaluates each completed conversation against the shared scenario rules. Exact cited evidence will appear here."}
          </p>
          {(e.evidence || []).map((ev: Obj, i: number) => (
            <button
              className="evidence-quote"
              key={i}
              onClick={() => onEvidence(c.model_id, ev.turn, ev.quote)}
            >
              <span>
                Turn {ev.turn} · Jump to exact quote <ArrowRight size={12} />
              </span>
              <blockquote dir="auto">“{ev.quote}”</blockquote>
              <small>{ev.rule}</small>
            </button>
          ))}
          {(c?.checks || []).length > 0 && (
            <div className="literal-checks">
              <span className="b-overline">LITERAL CHECKS</span>
              {c.checks.map((check: Obj, i: number) => (
                <span key={i}>
                  <Badge
                    tone={
                      check.passed === true
                        ? "teal"
                        : check.passed === false
                          ? "error"
                          : "neutral"
                    }
                  >
                    {check.passed === true
                      ? "Pass"
                      : check.passed === false
                        ? "Fail"
                        : "Unknown"}
                  </Badge>
                  {check.kind === "not_empty"
                    ? "Non-empty response"
                    : check.kind === "max_words"
                      ? "Maximum " + check.value + " words"
                      : check.kind === "contains"
                        ? "Contains “" + check.value + "”"
                        : "Excludes “" + check.value + "”"}
                </span>
              ))}
            </div>
          )}
          {e.human_review && (
            <p className="human-note">
              Human Review:{" "}
              {e.human_review.note || label(e.human_review.decision)}
            </p>
          )}
        </div>
        <div className="human-review">
          <span className="b-overline">HUMAN REVIEW</span>
          <ErrorBox error={error} />
          <select
            aria-label="Review Decision"
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            disabled={!c}
          >
            <option value="agree">Agree with Judgment</option>
            <option value="disagree">Correct the Outcome</option>
            <option value="cannot_determine">Unable to Determine</option>
          </select>
          {decision === "disagree" && (
            <select
              aria-label="Corrected Outcome"
              value={correct}
              onChange={(e) => setCorrect(e.target.value)}
            >
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
              <option value="critical">Critical failure</option>
            </select>
          )}
          <textarea
            aria-label="Review Note"
            placeholder="Add a reason or quote…"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={!c}
          />
          <button
            className="b-button"
            disabled={!c || busy || !run}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await request("/runs/" + run?.id + "/reviews", {
                  conversation_id: c.id,
                  decision,
                  note,
                  ...(decision === "disagree"
                    ? {
                        correct: correct === "pass",
                        critical: correct === "critical",
                      }
                    : {}),
                });
                onRefresh();
                setNote("");
                toast.success("Review saved");
              } catch (err) {
                setError(err);
              } finally {
                setBusy(false);
              }
            }}
          >
            <Check size={14} />
            Save Review
          </button>
          <small>
            Applies to this comparison. It does not change future judge policy.
          </small>
        </div>
      </div>
    </>
  );
}
function RunSettings({
  config,
  patch,
  data,
  onClose,
  preflight,
}: {
  config: Obj;
  patch: (k: string, v: unknown) => void;
  data: Bootstrap;
  onClose: () => void;
  preflight: string[];
}) {
  return (
    <Modal
      title="Run Settings"
      description="Choose the conversation count, load, and spending limit for this comparison."
      onClose={onClose}
      wide
    >
      <div className="b-grid two">
        <Field label="Conversation Mode">
          <select
            value={config.mode}
            onChange={(e) => patch("mode", e.target.value)}
          >
            <option value="replay">Replay · Same recorded turns</option>
            <option value="adaptive">
              Simulation · Same persona, different paths
            </option>
          </select>
        </Field>
        <Field label="Workload">
          <select
            value={config.profile}
            onChange={(e) => patch("profile", e.target.value)}
          >
            <option value="quality">Quality comparison</option>
            <option value="paced">Paced conversations</option>
            <option value="stress">Concurrency stress test</option>
          </select>
        </Field>
        <Field
          label="Active concurrent conversations"
          hint={config.model_schedule === "simultaneous" ? "Shared across all selected models, not multiplied by their count. Models take turns entering this shared limit; provider quotas may reduce active requests." : "Active conversation limit for each model. Models run sequentially; provider quotas may reduce active requests."}
        >
          <input
            type="number"
            min="1"
            max="400"
            value={config.concurrency}
            onChange={(e) => patch("concurrency", Number(e.target.value))}
          />
        </Field>
        <Field label="Conversation limit per model">
          <input
            type="number"
            min="1"
            max="100000"
            value={config.max_conversations}
            onChange={(e) => patch("max_conversations", Number(e.target.value))}
          />
        </Field>
        {config.mode === "adaptive" && (
          <Field
            label="Customer turn limit"
            hint="Leave blank for the source conversation length + 3 turns, capped at 100. The conversation can end naturally sooner."
          >
            <input
              type="number"
              min="1"
              max="100"
              placeholder="Source-based default"
              value={config.max_customer_turns ?? ""}
              onChange={(e) =>
                patch(
                  "max_customer_turns",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
            />
          </Field>
        )}
        <Field
          label="Spend cap · USD"
          hint="The worker reserves costs before admission and stops admitting work at the cap."
        >
          <input
            type="number"
            min="0.01"
            step="0.5"
            value={config.budget_usd}
            onChange={(e) => patch("budget_usd", Number(e.target.value))}
          />
        </Field>
        <Field
          label="Maximum provider requests"
          hint="Includes target, simulator, judge and retry calls."
        >
          <input
            type="number"
            min="1"
            value={config.max_requests}
            onChange={(e) => patch("max_requests", Number(e.target.value))}
          />
        </Field>
        <Field label="Run duration · seconds">
          <input
            type="number"
            min="1"
            value={config.measured_seconds}
            onChange={(e) => patch("measured_seconds", Number(e.target.value))}
          />
        </Field>
        <Field label="Request deadline · seconds">
          <input
            type="number"
            min="1"
            value={config.deadline_seconds}
            onChange={(e) => patch("deadline_seconds", Number(e.target.value))}
          />
        </Field>
        <Field label="Retries per request">
          <input
            type="number"
            min="0"
            max="5"
            value={config.retries}
            onChange={(e) => patch("retries", Number(e.target.value))}
          />
        </Field>
        <Field label="Customer think time · milliseconds">
          <input
            type="number"
            min="0"
            value={config.think_time_ms}
            onChange={(e) => patch("think_time_ms", Number(e.target.value))}
          />
        </Field>
        <Field label="Frozen date">
          <input
            type="date"
            value={config.clock_date}
            onChange={(e) => patch("clock_date", e.target.value)}
          />
        </Field>
        <Field label="Timezone">
          <input
            value={config.timezone}
            onChange={(e) => patch("timezone", e.target.value)}
          />
        </Field>
        <Field label="Judge profile">
          <select
            value={config.judge_model_id}
            onChange={(e) => patch("judge_model_id", e.target.value)}
          >
            <option value="">No judge · preserve ungraded outputs</option>
            {data.models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
        {config.mode === "adaptive" && (
          <Field
            label="Realism evaluator"
            hint="Reviews customer realism against the anonymized source. It is separate from policy grading."
          >
            <select
              value={config.realism_model_id || ""}
              onChange={(e) => patch("realism_model_id", e.target.value)}
            >
              <option value="">Use the selected judge profile</option>
              {data.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        {config.mode === "adaptive" && (
          <Field label="Customer simulator profile">
            <select
              value={config.simulator_model_id}
              onChange={(e) => patch("simulator_model_id", e.target.value)}
            >
              <option value="">Choose Simulator</option>
              {data.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <div className="estimate-panel">
        <div><span>Spending Limit</span><strong>{money(config.budget_usd)}</strong></div>
        <p>Up to {config.max_conversations} conversations per model. Scenarios repeat when needed. This limit covers models, simulation, judges, and retries. A request starts only when its maximum possible cost fits the remaining budget. Recorded cost uses provider usage and saved rates.</p>
      </div>
      {config.mode === "adaptive" && (
        <div className="b-alert warning">
          Adaptive customer dialogue can diverge. Results compare scenario-level
          outcomes, not identical later inputs.
        </div>
      )}
      {preflight.length > 0 && (
        <div className="b-alert warning">
          <div>
            <strong>Before starting</strong>
            <ul>
              {preflight.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div className="modal-actions">
        <button className="b-button primary" onClick={onClose}>
          <Check size={15} />
          Use these settings
        </button>
      </div>
    </Modal>
  );
}
function HistoryView({
  runs,
  onClose,
  onOpen,
  onNew,
}: {
  runs: Obj[];
  onClose: () => void;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const [query, setQuery] = useState("");
  const [showDemoHistory, setShowDemoHistory] = useState(false);
  const visibleRuns = runs.filter((r) => showDemoHistory || !r.all_demonstration);
  return (
    <Modal
      title="Comparison history"
      description="Reopen immutable configurations, outputs and review evidence."
      onClose={onClose}
      wide
    >
      <div className="section-toolbar">
        <div className="b-search">
          <Search size={15} />
          <input
            placeholder="Find a comparison"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button className="b-button primary" onClick={onNew}>
          <Plus size={15} />
          New Comparison
        </button>
      </div>
      {runs.some((r) => r.all_demonstration) && (
        <label className="b-check">
          <input type="checkbox" checked={showDemoHistory} onChange={(e) => setShowDemoHistory(e.target.checked)} />
          Show Archived Demo History
        </label>
      )}
      <table className="analytics-table history-table">
        <thead>
          <tr>
            <th>Comparison</th>
            <th>Status</th>
            <th>Mode</th>
            <th>Progress</th>
            <th>Estimated spend</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visibleRuns
            .filter((r) =>
              (r.name + " " + r.id).toLowerCase().includes(query.toLowerCase()),
            )
            .map((r) => (
              <tr key={r.id}>
                <td>
                  <strong>{r.name}</strong>
                  {r.all_demonstration && <Badge>Scripted Demo</Badge>}
                  <small>
                    {r.created_at
                      ? new Date(
                          typeof r.created_at === "number"
                            ? r.created_at * 1000
                            : r.created_at,
                        ).toLocaleString()
                      : r.id}
                  </small>
                </td>
                <td>
                  <Badge tone={r.status === "complete" ? "teal" : "neutral"}>
                    {label(r.status)}
                  </Badge>
                </td>
                <td>{label(r.config?.mode)}</td>
                <td>
                  {r.progress?.completed_conversations || 0} /{" "}
                  {r.progress?.total_conversations || "—"}
                </td>
                <td>{money(r.progress?.spend_usd)}</td>
                <td>
                  <button className="b-button" onClick={() => onOpen(r.id)}>
                    Open <ArrowRight size={13} />
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {!visibleRuns.length && (
        <Empty title="No comparisons yet">
          Your saved configurations and measured results will appear here.
        </Empty>
      )}
    </Modal>
  );
}
function SourceView({
  scenario,
  convs,
  models,
  mode,
}: {
  scenario: Obj | undefined;
  convs: Obj[];
  models: Model[];
  mode: string;
}) {
  const [model, setModel] = useState("");
  if (!scenario)
    return (
      <Empty title="Choose a scenario to inspect its provenance">
        Reference transcripts and generated scenarios remain distinguishable.
      </Empty>
    );
  const reference =
    scenario.reference_transcript ||
    scenario.source_transcript ||
    scenario.reference?.turns ||
    scenario.source_evidence?.transcript ||
    scenario.source_evidence?.turns;
  const source =
    scenario.source_metadata ||
    scenario.source_evidence ||
    scenario.provenance_details ||
    {};
  const observed = scenario.observed_facts || source.observed_facts;
  const invented =
    scenario.invented_facts ||
    scenario.synthetic_facts ||
    scenario.substitutions ||
    source.substitutions ||
    source.invented_facts;
  const realism =
    scenario.realism_qa || source.realism_qa || scenario.realism_review;
  const conv = convs.find((c) => c.model_id === model) || convs[0];
  const screen = conv?.realism;
  return (
    <div className="source-workspace">
      <div className="analytics-heading">
        <div>
          <h2>Source & Realism</h2>
          <p>
            Compare the anonymized reference, constructed scenario and measured
            model conversation.
          </p>
        </div>
        <Badge>{label(scenario.provenance)}</Badge>
        <Badge tone={realism?.status === "approved" ? "teal" : "warning"}>
          Realism {realism?.status || "not reviewed"}
        </Badge>
      </div>
      <div className="source-provenance">
        <span>
          Source group <code>{scenario.source_group}</code>
        </span>
        <span>
          Source ID{" "}
          <code>{source.id || scenario.source_id || "Not attached"}</code>
        </span>
        <span>
          Scenario review <strong>{label(scenario.review_status)}</strong>
        </span>
      </div>
      <div className="b-alert">
        <div>
          {mode === "replay"
            ? "Replay uses frozen customer turns from the reference. It does not validate a customer simulator."
            : "Adaptive dialogue screens the simulated customer against the source; policy quality is graded separately."}
          <br />
          {source.transcript_completeness &&
            "Reference completeness: " + source.transcript_completeness + ". "}
          {source.audio_verified === false && "Audio has not been verified. "}
          {source.historical_agent_is_expected_answer === false &&
            "Historical agent replies are evidence, not expected correct answers."}
        </div>
      </div>
      {screen && <RealismMetrics screen={screen} replay={mode === "replay"} />}
      {(conv?.effective_realism_evaluation || conv?.realism_evaluation) && (
        <SemanticRealism result={conv.effective_realism_evaluation || conv.realism_evaluation} />
      )}
      <div className="source-columns">
        <section>
          <div className="pane-heading">
            <strong>Real Transcript</strong>
            <Badge>Observed</Badge>
          </div>
          <div className="source-scroll">
            {reference ? (
              <TranscriptData value={reference} />
            ) : (
              <Empty title="No reference transcript attached">
                This scenario cannot yet be assessed for resemblance to a
                recorded customer conversation. A prompt-derived draft is not
                real-call evidence.
              </Empty>
            )}
          </div>
        </section>
        <section>
          <div className="pane-heading">
            <strong>Shared Scenario</strong>
            <Badge>Constructed</Badge>
          </div>
          <div className="source-scroll">
            <h3>{scenario.name}</h3>
            <p dir="auto">{scenario.persona || scenario.scenario}</p>
            <div className="source-label">EXPECTED BEHAVIOR</div>
            <div className="context-expectations" dir="auto">
              <FactList value={scenario.expected_behavior} />
            </div>
            <div className="source-label">FROZEN CUSTOMER INPUT</div>
            {(scenario.checkpoints?.length
              ? scenario.checkpoints.map((c: Obj) => c.user_turn)
              : scenario.user_turns || []
            ).map((t: string, i: number) => (
              <blockquote dir="auto" key={i}>
                {t}
              </blockquote>
            ))}
            <div className="source-label">OBSERVED FACTS</div>
            {observed ? (
              <FactList value={observed} />
            ) : (
              <p className="b-muted">
                Facts have not been separately labeled as observed.
              </p>
            )}
            <div className="source-label">INVENTED / SUBSTITUTED FACTS</div>
            {invented ? (
              <FactList value={invented} />
            ) : (
              <p className="b-muted">
                No explicit invention/substitution record attached.
              </p>
            )}
            <details>
              <summary>All supplied scenario facts</summary>
              <FactList value={scenario.facts || {}} />
            </details>
            <div className="source-label">REALISM QA</div>
            <p>
              {realism?.note ||
                realism?.rationale ||
                "Not reviewed. Realism review is independent of policy correctness and judge grading."}
            </p>
          </div>
        </section>
        <section>
          <div className="pane-heading">
            <strong>Model conversation</strong>
          </div>
          <select
            aria-label="Model for source comparison"
            value={conv?.model_id || model}
            onChange={(e) => setModel(e.target.value)}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <div className="source-scroll">
            {conv?.turns?.length ? (
              conv.turns.map((t: Obj) => (
                <div className="source-turn" key={t.index}>
                  <span>Customer · {t.index}</span>
                  <p dir="auto">{t.user}</p>
                  <span>Model response</span>
                  <p dir="auto">{t.assistant}</p>
                </div>
              ))
            ) : (
              <Empty title="No simulation yet">
                Run this shared scenario to inspect how closely the dialogue
                resembles its reference.
              </Empty>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
function TranscriptData({ value }: { value: unknown }) {
  if (typeof value === "string")
    return (
      <pre dir="auto" className="reference-text">
        {value}
      </pre>
    );
  if (Array.isArray(value))
    return (
      <>
        {value.map((t, i) => (
          <div className="source-turn" key={i}>
            <span>{label(t.role || t.speaker || "Turn " + (i + 1))}</span>
            <p dir="auto">
              {t.content || t.text || t.user || JSON.stringify(t)}
            </p>
            {t.assistant && (
              <>
                <span>Agent</span>
                <p dir="auto">{t.assistant}</p>
              </>
            )}
          </div>
        ))}
      </>
    );
  return <pre className="reference-text">{JSON.stringify(value, null, 2)}</pre>;
}
function FactList({ value }: { value: unknown }) {
  if (Array.isArray(value))
    return value.length ? (
      <ul className="fact-notes">
        {value.map((v, i) => (
          <li key={i} dir="auto">
            <FactList value={v} />
          </li>
        ))}
      </ul>
    ) : (
      <span className="b-muted">None recorded</span>
    );
  if (value && typeof value === "object")
    return (
      <dl className="fact-list">
        {Object.entries(value).map(([key, v]) => (
          <div
            key={key}
            className={v && typeof v === "object" ? "fact-group" : ""}
          >
            <dt>
              {key === "runtime_bindings"
                ? "Context supplied to the model"
                : key === "dpd"
                  ? "Days past due"
                  : label(key.replace(/([a-z])([A-Z])/g, "$1 $2"))}
            </dt>
            <dd dir="auto">
              <FactList value={v} />
            </dd>
          </div>
        ))}
      </dl>
    );
  if (value == null || value === "")
    return <span className="b-muted">Not recorded</span>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (typeof value === "number")
    return (
      <span>
        {value.toLocaleString("en-US", { maximumFractionDigits: 10 })}
      </span>
    );
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(value))
  )
    return (
      <time dateTime={value}>
        {new Intl.DateTimeFormat("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        }).format(new Date(value))}
      </time>
    );
  return <span>{String(value)}</span>;
}
function CheckpointMatrix({
  models,
  convs,
  onChoose,
}: {
  models: Model[];
  convs: Obj[];
  onChoose: (mid: string, key: string) => void;
}) {
  const rows = new Map<string, Obj>();
  for (const c of convs)
    for (const cp of evaluation(c).checkpoint_evaluations || [])
      for (const r of cp.criteria || [])
        rows.set((cp.checkpoint_id || "adaptive") + ":" + r.rule_id, {
          ...r,
          checkpoint_id: cp.checkpoint_id,
        });
  if (!rows.size) return null;
  return (
    <div className="checkpoint-matrix">
      <table>
        <thead>
          <tr>
            <th>Criterion / checkpoint</th>
            {models.map((m) => (
              <th key={m.id}>{m.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...rows].map(([key, rule]) => (
            <tr key={key}>
              <td>
                <span>{rule.text}</span>
                <small>
                  {rule.checkpoint_id
                    ? "Checkpoint " + rule.checkpoint_id
                    : "Adaptive conversation"}{" "}
                  · {label(rule.severity)}
                </small>
              </td>
              {models.map((m) => {
                const c = convs.find((c) => c.model_id === m.id);
                const cp = (evaluation(c).checkpoint_evaluations || []).find(
                  (x: Obj) => x.checkpoint_id === rule.checkpoint_id,
                );
                const r = cp?.criteria?.find(
                  (x: Obj) => x.rule_id === rule.rule_id,
                );
                return (
                  <td key={m.id}>
                    <button onClick={() => onChoose(m.id, key)} disabled={!r}>
                      <Badge
                        tone={
                          r?.outcome === "pass"
                            ? "teal"
                            : r?.outcome === "fail"
                              ? "error"
                              : "neutral"
                        }
                      >
                        {r ? label(r.outcome) : "Not evaluated"}
                      </Badge>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Criterion rows are immutable machine judgments. Any human correction to
        the overall outcome is shown separately.
      </p>
    </div>
  );
}
function RealismMetrics({ screen, replay }: { screen: Obj; replay: boolean }) {
  const source = screen.source_profile,
    sim = screen.simulated_profile;
  return (
    <section className="realism-panel">
      <div className="section-toolbar">
        <h3>Conversation Patterns</h3>
        <Badge
          tone={
            replay ? "neutral" : screen.status === "failed"
              ? "error"
              : screen.status === "screen_passed"
                ? "teal"
                : "warning"
          }
        >
          {replay ? "Recorded Customer Turns" : screen.status === "screen_passed"
            ? "Style Checks Passed"
            : label(screen.status)}
        </Badge>
        <Badge>
          {screen.human_reviewed
            ? "Human Review Recorded"
            : "Human Review Pending"}
        </Badge>
      </div>
      <p className="b-muted">
        {replay ? "See the length, pauses, and language patterns in the recorded customer turns." : screen.scope || "Checks conversation style against the source. Quality and human realism reviews are shown separately."}
      </p>
      {(screen.reasons || []).map((r: string) => (
        <p className="b-muted" key={r}>
          {r}
        </p>
      ))}
      {source && sim && (
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Conversation Feature</th>
              <th>Real Transcript</th>
              <th>{replay ? "Recorded Turns" : "Simulation"}</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["turns", "Turns"],
              ["median_words", "Median words / turn"],
              ["p90_words", "p90 words / turn"],
              ["short_turn_fraction", "Short replies (≤5 words)"],
              ["arabic_letter_fraction", "Arabic letter share"],
              ["mixed_language_turns", "Mixed-language turns"],
              ["meta_language_turns", "Benchmark instruction leaks"],
            ].map(([k, n]) => (
              <tr key={k}>
                <td>{n}</td>
                <td>
                  {typeof source.customer?.[k] === "number"
                    ? k.includes("fraction")
                      ? (source.customer[k] * 100).toFixed(0) + "%"
                      : number(source.customer[k])
                    : "—"}
                </td>
                <td>
                  {typeof sim.customer?.[k] === "number"
                    ? k.includes("fraction")
                      ? (sim.customer[k] * 100).toFixed(0) + "%"
                      : number(sim.customer[k])
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
function SemanticRealism({ result }: { result: Obj }) {
  return (
    <section className="realism-panel">
      <div className="section-toolbar">
        <h3>AI realism review · customer dialogue</h3>
        <Badge
          tone={
            result.status === "failed"
              ? "error"
              : result.status === "ai_screen_passed"
                ? "teal"
                : "warning"
          }
        >
          {result.status === "ai_screen_passed"
            ? "AI Review Passed · Provisional"
            : label(result.status)}
        </Badge>
        <Badge>Not human validated</Badge>
      </div>
      <p className="b-muted">
        {result.reason ||
          result.threshold ||
          "Source-grounded review of customer behavior. This does not replace human realism assessment or policy evaluation."}
      </p>
      {result.dimensions?.length > 0 && (
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Dimension</th>
              <th>Score</th>
              <th>Exact source evidence</th>
              <th>Exact simulation evidence</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {result.dimensions.map((d: Obj) => (
              <tr key={d.id}>
                <td>{label(d.id)}</td>
                <td>
                  {typeof d.score === "number"
                    ? `${d.score} / 5`
                    : "Not assessed"}
                </td>
                <td dir="auto">
                  {d.source_quote ? (
                    <>
                      {d.source_turn != null && (
                        <small>Turn {d.source_turn}</small>
                      )}
                      “{d.source_quote}”
                    </>
                  ) : (
                    "No evidence"
                  )}
                </td>
                <td dir="auto">
                  {d.simulation_quote ? (
                    <>
                      {d.simulation_turn != null && (
                        <small>Turn {d.simulation_turn}</small>
                      )}
                      “{d.simulation_quote}”
                    </>
                  ) : (
                    "No evidence"
                  )}
                </td>
                <td>{d.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {result.contradictions?.length > 0 && (
        <div className="b-alert error">
          <div>
            <strong>Contradictory facts</strong>
            <FactList value={result.contradictions} />
          </div>
        </div>
      )}
    </section>
  );
}
